import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

type ToolActivityCountCopy = {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
};

const COPY_BY_LOCALE: Record<SupportedLocale, ToolActivityCountCopy> = {
  en: { one: '{count} update', other: '{count} updates' },
  'pt-BR': {
    one: '{count} atualiza\u00E7\u00E3o',
    other: '{count} atualiza\u00E7\u00F5es',
  },
  ar: {
    zero: 'لا تحديثات',
    one: 'تحديث واحد',
    two: 'تحديثان',
    few: '{count} تحديثات',
    many: '{count} تحديثًا',
    other: '{count} تحديث',
  },
  es: { one: '{count} actualización', other: '{count} actualizaciones' },
  fr: { one: '{count} mise à jour', other: '{count} mises à jour' },
  it: { one: '{count} aggiornamento', other: '{count} aggiornamenti' },
  ko: { one: '업데이트 {count}개', other: '업데이트 {count}개' },
  de: { one: '{count} Aktualisierung', other: '{count} Aktualisierungen' },
  ja: { one: '{count} 件の更新', other: '{count} 件の更新' },
  hi: { one: '{count} अपडेट', other: '{count} अपडेट' },
  ru: {
    one: '{count} обновление',
    few: '{count} обновления',
    many: '{count} обновлений',
    other: '{count} обновления',
  },
  zh: { one: '{count} 项更新', other: '{count} 项更新' },
  'zh-TW': { one: '{count} 項更新', other: '{count} 項更新' },
  vi: { one: '{count} cập nhật', other: '{count} cập nhật' },
  id: { one: '{count} pembaruan', other: '{count} pembaruan' },
};

const FORMATTERS_BY_LOCALE = Object.freeze({
  en: {
    pluralRules: new Intl.PluralRules('en'),
    numberFormat: new Intl.NumberFormat('en'),
  },
  'pt-BR': {
    pluralRules: new Intl.PluralRules('en'),
    numberFormat: new Intl.NumberFormat('en'),
  },
  ar: {
    pluralRules: new Intl.PluralRules('ar'),
    numberFormat: new Intl.NumberFormat('ar'),
  },
  es: {
    pluralRules: new Intl.PluralRules('es'),
    numberFormat: new Intl.NumberFormat('es'),
  },
  fr: {
    pluralRules: new Intl.PluralRules('fr'),
    numberFormat: new Intl.NumberFormat('fr'),
  },
  it: {
    pluralRules: new Intl.PluralRules('it'),
    numberFormat: new Intl.NumberFormat('it'),
  },
  ko: {
    pluralRules: new Intl.PluralRules('ko'),
    numberFormat: new Intl.NumberFormat('ko'),
  },
  de: {
    pluralRules: new Intl.PluralRules('de'),
    numberFormat: new Intl.NumberFormat('de'),
  },
  ja: {
    pluralRules: new Intl.PluralRules('ja'),
    numberFormat: new Intl.NumberFormat('ja'),
  },
  hi: {
    pluralRules: new Intl.PluralRules('hi'),
    numberFormat: new Intl.NumberFormat('hi'),
  },
  ru: {
    pluralRules: new Intl.PluralRules('ru'),
    numberFormat: new Intl.NumberFormat('ru'),
  },
  zh: {
    pluralRules: new Intl.PluralRules('zh'),
    numberFormat: new Intl.NumberFormat('zh'),
  },
  'zh-TW': {
    pluralRules: new Intl.PluralRules('zh-TW'),
    numberFormat: new Intl.NumberFormat('zh-TW'),
  },
  vi: {
    pluralRules: new Intl.PluralRules('vi'),
    numberFormat: new Intl.NumberFormat('vi'),
  },
  id: {
    pluralRules: new Intl.PluralRules('id'),
    numberFormat: new Intl.NumberFormat('id'),
  },
}) satisfies Readonly<
  Record<
    SupportedLocale,
    {
      readonly pluralRules: Intl.PluralRules;
      readonly numberFormat: Intl.NumberFormat;
    }
  >
>;

export function getToolActivityCountLabel(
  locale: string | null | undefined,
  count: number
): string {
  const normalizedLocale = normalizeLocale(locale);
  const copy = COPY_BY_LOCALE[normalizedLocale];
  const formatters = FORMATTERS_BY_LOCALE[normalizedLocale];
  const pluralCategory = formatters.pluralRules.select(count);
  const template = copy[pluralCategory] ?? copy.other;
  const formattedCount = formatters.numberFormat.format(count);

  return template.replace('{count}', formattedCount);
}
